
import {EventDispatcher} from "../EventDispatcher.js";
import {SVGProfileExporter} from "../exporter/SVGProfileExporter.js";

export class CrossSectionWindow extends EventDispatcher {

	constructor(viewer) {
		super();

		this.viewer = viewer;
		this.elRoot = $("#cross_section_window");
		this.currentProfile = null;

		// Close button
		let closeBtn = $("#closeCrossSectionWindow");
		closeBtn.attr("src", `${Potree.resourcePath}/icons/close.svg`);
		closeBtn.click(() => {
			this.hide();
		});

		// Swath width slider
		let swathSlider = document.getElementById("cs_swath_slider");
		let swathLabel = document.getElementById("cs_swath_label");

		swathSlider.addEventListener("input", () => {
			let feet = parseFloat(swathSlider.value);
			swathLabel.textContent = feet.toFixed(1) + " ft";

			if (this.currentProfile) {
				this.currentProfile.swathWidth = feet * 0.3048;
				this.dispatchEvent({ type: "swath_changed", profile: this.currentProfile });
			}
		});

		// Outlier filter toggle
		let outlierToggle = document.getElementById("cs_outlier_toggle");
		outlierToggle.addEventListener("change", () => {
			if (this.currentProfile) {
				this.currentProfile.outlierFilter.enabled = outlierToggle.checked;
				this.dispatchEvent({ type: "outlier_changed", profile: this.currentProfile });
			}
		});

		// Outlier threshold
		let outlierThreshold = document.getElementById("cs_outlier_threshold");
		outlierThreshold.addEventListener("change", () => {
			if (this.currentProfile) {
				this.currentProfile.outlierFilter.threshold = parseFloat(outlierThreshold.value);
				this.dispatchEvent({ type: "outlier_changed", profile: this.currentProfile });
			}
		});

		// Reset zoom button
		let resetZoom = document.getElementById("cs_reset_zoom");
		resetZoom.addEventListener("click", () => {
			if (this.currentProfile) {
				this.drawChart(this.currentProfile);
			}
		});

		// Export SVG button
		let exportBtn = document.getElementById("cs_export_svg");
		exportBtn.addEventListener("click", () => {
			if (!this.currentProfile) { return; }

			// Adapt camelCase profile to snake_case expected by SVGProfileExporter
			let exportData = this._toExportFormat(this.currentProfile);

			let svgString = SVGProfileExporter.toString(exportData, {
				title: "Cross-Section Profile",
				showPoints: true,
			});

			let blob = new Blob([svgString], { type: "image/svg+xml" });
			let url = URL.createObjectURL(blob);
			let a = document.createElement("a");
			a.href = url;
			a.download = "cross-section-profile.svg";
			document.body.appendChild(a);
			a.click();
			document.body.removeChild(a);
			URL.revokeObjectURL(url);
		});
	}

	show(profile) {
		this.currentProfile = profile;
		this.elRoot.fadeIn();

		// Update controls from profile state
		let swathFeet = profile.swathWidth / 0.3048;
		document.getElementById("cs_swath_slider").value = swathFeet;
		document.getElementById("cs_swath_label").textContent = swathFeet.toFixed(1) + " ft";
		document.getElementById("cs_outlier_toggle").checked = profile.outlierFilter.enabled;
		document.getElementById("cs_outlier_threshold").value = profile.outlierFilter.threshold;

		this.drawChart(profile);
	}

	hide() {
		this.elRoot.fadeOut();
		this.currentProfile = null;
	}

	drawChart(profile) {
		let svg = d3.select("#cross_section_svg");
		svg.selectAll("*").remove();

		let points = profile.points;
		let rawPoints = profile.pointsRaw;
		if (points.length === 0) { return; }

		let container = document.getElementById("cross_section_svg");
		let rect = container.getBoundingClientRect();
		let totalWidth = rect.width;
		let totalHeight = rect.height;

		let margin = { top: 20, right: 30, bottom: 45, left: 70 };
		let width = totalWidth - margin.left - margin.right;
		let availableHeight = totalHeight - margin.top - margin.bottom;

		// Unit conversion: meters to feet
		let feetPerMeter = 3.28084;

		// X scale: distance along transect in feet
		let xExtent = [0, profile.length * feetPerMeter];
		let distRangeFt = xExtent[1] - xExtent[0];
		let xScale = d3.scale.linear().domain(xExtent).range([0, width]);

		// Y scale: elevation in feet (based on smoothed points)
		let allElevFt = points.map(p => p.elevation * feetPerMeter);
		let yMin = d3.min(allElevFt);
		let yMax = d3.max(allElevFt);
		let yPadding = (yMax - yMin) * 0.1 || 0.5;

		// Vertical exaggeration — keep in sync with SVGProfileExporter.js
		const VE_MULTIPLIER = 10;
		let pxPerFoot = width / distRangeFt;
		let elevRangeFt = (yMax + yPadding) - (yMin - yPadding);
		let height = elevRangeFt * pxPerFoot * VE_MULTIPLIER;

		// Enforce minimum so the line is visible
		const MIN_PLOT_HEIGHT = 40;
		if (height < MIN_PLOT_HEIGHT) {
			height = MIN_PLOT_HEIGHT;
			elevRangeFt = height / pxPerFoot;
			let elevCenter = (yMin + yMax) / 2;
			yMin = elevCenter - elevRangeFt / 2;
			yMax = elevCenter + elevRangeFt / 2;
			yPadding = 0;
		}

		// Cap height to available space; widen Y domain so data isn't clipped
		if (height > availableHeight) {
			height = availableHeight;
			elevRangeFt = height / (pxPerFoot * VE_MULTIPLIER);
			let elevCenter = (yMin + yMax) / 2;
			yMin = elevCenter - elevRangeFt / 2;
			yMax = elevCenter + elevRangeFt / 2;
			yPadding = 0;
		}

		// Center vertically if plot is shorter than available space
		let extraVertical = availableHeight - height;
		if (extraVertical > 0) {
			margin.top += extraVertical / 2;
		}

		let yScale = d3.scale.linear()
			.domain([yMin - yPadding, yMax + yPadding])
			.range([height, 0]);

		// Store original domains for zoom reset
		let xDomain0 = xScale.domain().slice();
		let yDomain0 = yScale.domain().slice();

		// Clip path for chart area
		svg.append("defs").append("clipPath")
			.attr("id", "cs-clip")
			.append("rect")
			.attr("width", width)
			.attr("height", height);

		let g = svg.append("g")
			.attr("transform", "translate(" + margin.left + "," + margin.top + ")");

		// Chart background (for zoom interaction)
		g.append("rect")
			.attr("width", width)
			.attr("height", height)
			.attr("fill", "transparent");

		// Clipped content group
		let chartArea = g.append("g")
			.attr("clip-path", "url(#cs-clip)");

		// X axis
		let xAxis = d3.svg.axis().scale(xScale).orient("bottom").ticks(10);
		let xAxisG = g.append("g")
			.attr("transform", "translate(0," + height + ")")
			.call(xAxis);
		xAxisG.selectAll("text, line, path")
			.style("fill", "#9d9d9d")
			.style("stroke", "#9d9d9d");

		// X axis label
		g.append("text")
			.attr("x", width / 2)
			.attr("y", height + 38)
			.attr("text-anchor", "middle")
			.style("fill", "#9d9d9d")
			.style("font-size", "12px")
			.text("Distance (ft)");

		// Y axis
		let yAxis = d3.svg.axis().scale(yScale).orient("left").ticks(Math.max(Math.floor(height / 40), 3));
		let yAxisG = g.append("g")
			.call(yAxis);
		yAxisG.selectAll("text, line, path")
			.style("fill", "#9d9d9d")
			.style("stroke", "#9d9d9d");

		// Y axis label
		g.append("text")
			.attr("transform", "rotate(-90)")
			.attr("y", -55)
			.attr("x", -height / 2)
			.attr("text-anchor", "middle")
			.style("fill", "#9d9d9d")
			.style("font-size", "12px")
			.text("Elevation (ft)");

		// Smoothed profile line
		let line = d3.svg.line()
			.x(d => xScale(d.distanceAlong * feetPerMeter))
			.y(d => yScale(d.elevation * feetPerMeter))
			.interpolate("monotone");

		chartArea.append("path")
			.datum(points)
			.attr("d", line)
			.attr("fill", "none")
			.attr("stroke", "#00cc66")
			.attr("stroke-width", 2.5);

		// Landmark markers (vertical dashed lines with labels)
		for (let lm of profile.intersectingLandmarks) {
			let xPos = xScale(lm.distance_along * feetPerMeter);

			chartArea.append("line")
				.attr("x1", xPos).attr("x2", xPos)
				.attr("y1", 0).attr("y2", height)
				.attr("stroke", "#ff9900")
				.attr("stroke-width", 1.5)
				.attr("stroke-dasharray", "4,3");

			chartArea.append("text")
				.attr("x", xPos + 3)
				.attr("y", 12)
				.style("fill", "#ff9900")
				.style("font-size", "10px")
				.text(lm.label);
		}

		// Curb reveal markers (vertical dashed lines, different color)
		for (let cr of profile.intersectingCurbReveals) {
			let xPos = xScale(cr.distance_along * feetPerMeter);

			chartArea.append("line")
				.attr("x1", xPos).attr("x2", xPos)
				.attr("y1", 0).attr("y2", height)
				.attr("stroke", "#cc0066")
				.attr("stroke-width", 1.5)
				.attr("stroke-dasharray", "4,3");

			chartArea.append("text")
				.attr("x", xPos + 3)
				.attr("y", 24)
				.style("fill", "#cc0066")
				.style("font-size", "10px")
				.text(cr.curb_height.toFixed(1) + " in");
		}

		// Curb-to-curb distance indicator (outside clip path, below X axis)
		if (profile.intersectingCurbReveals.length === 2) {
			let cr0 = profile.intersectingCurbReveals[0];
			let cr1 = profile.intersectingCurbReveals[1];
			let x1 = xScale(Math.min(cr0.distance_along, cr1.distance_along) * feetPerMeter);
			let x2 = xScale(Math.max(cr0.distance_along, cr1.distance_along) * feetPerMeter);
			let lineY = height - 12;
			let distFt = Math.abs(cr0.distance_along - cr1.distance_along) * feetPerMeter;

			// Horizontal line
			chartArea.append("line")
				.attr("x1", x1).attr("x2", x2)
				.attr("y1", lineY).attr("y2", lineY)
				.attr("stroke", "#ffffff")
				.attr("stroke-width", 1);

			// Left tick
			chartArea.append("line")
				.attr("x1", x1).attr("x2", x1)
				.attr("y1", lineY - 4).attr("y2", lineY + 4)
				.attr("stroke", "#ffffff")
				.attr("stroke-width", 1);

			// Right tick
			chartArea.append("line")
				.attr("x1", x2).attr("x2", x2)
				.attr("y1", lineY - 4).attr("y2", lineY + 4)
				.attr("stroke", "#ffffff")
				.attr("stroke-width", 1);

			// Centered distance label
			chartArea.append("text")
				.attr("x", (x1 + x2) / 2)
				.attr("y", lineY - 6)
				.attr("text-anchor", "middle")
				.style("fill", "#ffffff")
				.style("font-size", "10px")
				.text(distFt.toFixed(1) + " ft");
		}

		// High point marker
		if (profile.computedMetrics.highPoint) {
			let hp = profile.computedMetrics.highPoint;
			let hx = xScale(hp.distance_along * feetPerMeter);
			let hy = yScale(hp.elevation * feetPerMeter);

			chartArea.append("polygon")
				.attr("points", this.diamondPoints(hx, hy, 6))
				.attr("fill", "#44ff44")
				.attr("stroke", "white")
				.attr("stroke-width", 1);

			g.append("text")
				.attr("x", hx + 8)
				.attr("y", hy - 5)
				.style("fill", "#44ff44")
				.style("font-size", "10px")
				.text("Hi: " + (hp.elevation * feetPerMeter).toFixed(2) + " ft");
		}

		// Low point marker
		if (profile.computedMetrics.lowPoint) {
			let lp = profile.computedMetrics.lowPoint;
			let lx = xScale(lp.distance_along * feetPerMeter);
			let ly = yScale(lp.elevation * feetPerMeter);

			chartArea.append("polygon")
				.attr("points", this.diamondPoints(lx, ly, 6))
				.attr("fill", "#ff4444")
				.attr("stroke", "white")
				.attr("stroke-width", 1);

			g.append("text")
				.attr("x", lx + 8)
				.attr("y", ly + 12)
				.style("fill", "#ff4444")
				.style("font-size", "10px")
				.text("Lo: " + (lp.elevation * feetPerMeter).toFixed(2) + " ft");
		}

		// Zoom behavior
		let zoom = d3.behavior.zoom()
			.x(xScale)
			.y(yScale)
			.on("zoom", () => {
				// Redraw axes
				xAxisG.call(xAxis);
				xAxisG.selectAll("text, line, path")
					.style("fill", "#9d9d9d")
					.style("stroke", "#9d9d9d");

				yAxisG.call(yAxis);
				yAxisG.selectAll("text, line, path")
					.style("fill", "#9d9d9d")
					.style("stroke", "#9d9d9d");

				// Redraw scatter points
				chartArea.selectAll(".raw-point")
					.attr("cx", d => xScale(d.distanceAlong * feetPerMeter))
					.attr("cy", d => yScale(d.elevation * feetPerMeter));

				// Redraw profile line
				chartArea.select("path")
					.attr("d", line);

				// Redraw landmark markers
				let lmLines = chartArea.selectAll("line");
				// Landmark + curb reveal vertical lines need repositioning
				// Simpler to redraw, but for zoom perf just update positions
			});

		g.call(zoom);
	}

	// Convert camelCase profile to snake_case format expected by SVGProfileExporter,
	// and convert meters to feet to match the viewer chart display units.
	_toExportFormat(profile) {
		const ft = 3.28084;

		// Map points: distanceAlong -> distance_along, convert m -> ft
		let mappedPoints = (profile.points || []).map(p => ({
			distance_along: p.distanceAlong * ft,
			elevation: p.elevation * ft,
		}));

		let mappedLandmarks = (profile.intersectingLandmarks || []).map(lm => {
			let out = { ...lm, distance_along: lm.distance_along * ft, elevation: lm.elevation * ft };
			if (lm.datum && lm.datum.reference_elevation != null) {
				out.datum = { ...lm.datum, reference_elevation: lm.datum.reference_elevation * ft };
			}
			return out;
		});

		let mappedCurbReveals = (profile.intersectingCurbReveals || []).map(cr => ({
			...cr,
			distance_along: cr.distance_along * ft,
			// curb_height is already in inches — no conversion needed
		}));

		let mappedHighPoint = null;
		if (profile.computedMetrics && profile.computedMetrics.highPoint) {
			let hp = profile.computedMetrics.highPoint;
			mappedHighPoint = { ...hp, distance_along: hp.distance_along * ft, elevation: hp.elevation * ft };
		}

		let mappedLowPoint = null;
		if (profile.computedMetrics && profile.computedMetrics.lowPoint) {
			let lp = profile.computedMetrics.lowPoint;
			mappedLowPoint = { ...lp, distance_along: lp.distance_along * ft, elevation: lp.elevation * ft };
		}

		let mappedSlopes = (profile.computedMetrics ? profile.computedMetrics.slopes : []).map(s => {
			let out = { ...s };
			if (s.distance != null) out.distance = s.distance * ft;
			if (s._from_distance != null) out._from_distance = s._from_distance * ft;
			if (s._to_distance != null) out._to_distance = s._to_distance * ft;
			return out;
		});

		return {
			points: mappedPoints,
			length: profile.length * ft,
			intersecting_landmarks: mappedLandmarks,
			intersecting_curb_reveals: mappedCurbReveals,
			computed_metrics: {
				high_point: mappedHighPoint,
				low_point: mappedLowPoint,
				slopes: mappedSlopes,
			},
			datum_offset: profile.datumOffset ? {
				applied: profile.datumOffset.applied,
				reference_landmark_id: profile.datumOffset.referenceLandmarkId,
				offset_value: profile.datumOffset.offsetValue != null
					? profile.datumOffset.offsetValue * ft : null,
			} : null,
		};
	}

	// Generate SVG points string for a diamond shape
	diamondPoints(cx, cy, r) {
		return [
			(cx) + "," + (cy - r),
			(cx + r) + "," + (cy),
			(cx) + "," + (cy + r),
			(cx - r) + "," + (cy)
		].join(" ");
	}
}
